import { UPLOAD_SCENE, type UploadScene } from '@/app/constants';
import type {
  FormAudioPrompt,
  FormImagePrompt,
  FormSubtitlePrompt,
  FormVideoPrompt,
  ImagePrompt,
  SourceAudio,
  SourceSubtitle,
  SourceVideo,
  VideoPrompt,
} from '@/app/types';
import { extractTempImageData, type UploadResult, uploadFile, uploadTempImages } from '@/app/utils/client';
import type { TempUploadItem, TempUploadRefs } from '../../types';
import { toolTempUploadKey } from '../_hooks/use-run-temp-upload-batch';
import type { FormPromptListHelpers } from '../types';

type TempVideoPrompt = VideoPrompt & {
  _tempFile?: File;
  _tempCoverImage?: Blob;
  width?: number;
  height?: number;
};

function isKeyScheduled(refs: TempUploadRefs, key: string): boolean {
  return refs.processed.current.has(key) || refs.pending.current.has(key);
}

async function uploadVideoFileWithCover(
  file: File,
  options: { width: number; height: number; duration?: number },
  coverImage?: Blob,
  scene: UploadScene = UPLOAD_SCENE.VIDU,
) {
  return Promise.all([
    uploadFile({ file, ...options, scene }),
    coverImage ? uploadFile({ file: coverImage, scene }) : Promise.resolve(null),
  ]);
}

function buildUploadedImageFields(
  currentPrompt: ImagePrompt,
  result: { sourceUri: string; selectedUri?: string },
): ImagePrompt {
  const { _tempFile, _tempSelectedImage, ...promptWithoutTemp } = currentPrompt;
  return {
    ...promptWithoutTemp,
    sourceImage: result.sourceUri,
    sourceImagePreview: currentPrompt.sourceImagePreview,
    sourceImageWidth: currentPrompt.sourceImageWidth ?? 0,
    sourceImageHeight: currentPrompt.sourceImageHeight ?? 0,
    selectedImage: result.selectedUri ?? result.sourceUri,
    selectedImagePreview: result.selectedUri
      ? currentPrompt.selectedImagePreview || currentPrompt.sourceImagePreview
      : currentPrompt.sourceImagePreview,
    selectedImageWidth: result.selectedUri ? currentPrompt.selectedImageWidth : (currentPrompt.sourceImageWidth ?? 0),
    selectedImageHeight: result.selectedUri
      ? currentPrompt.selectedImageHeight
      : (currentPrompt.sourceImageHeight ?? 0),
    selectedImageRange: promptWithoutTemp.selectedImageRange,
  };
}

export function getToolTempAudioPendingItems(
  refs: TempUploadRefs,
  helpers: FormPromptListHelpers<FormAudioPrompt>,
  ctx: { audioPrompts: Array<FormAudioPrompt | null>; onUploadError?: (error: UploadResult['error']) => void },
): TempUploadItem[] {
  const { updatePromptItem, markPromptStatus } = helpers;
  const pendingUploads = ctx.audioPrompts
    .map((prompt) => {
      if (!prompt || !prompt.prompt?._tempFile) return null;
      const uploadKey = toolTempUploadKey('audio', prompt.id);
      if (isKeyScheduled(refs, uploadKey)) return null;
      return { prompt, uploadKey };
    })
    .filter((item): item is { prompt: FormAudioPrompt; uploadKey: string } => item !== null);

  return pendingUploads.map(({ prompt, uploadKey }) => ({
    uploadKey,
    run: async (): Promise<void> => {
      markPromptStatus(prompt.id, 'uploading');
      const currentPrompt = prompt.prompt;
      if (!currentPrompt?._tempFile) {
        markPromptStatus(prompt.id, 'done');
        return;
      }
      const uploadResult = await uploadFile({
        file: currentPrompt._tempFile,
        duration: currentPrompt.duration,
        scene: UPLOAD_SCENE.VIDU,
      });
      if (uploadResult.error || !uploadResult.uri) {
        console.error('Failed to upload temp audio:', prompt.id, uploadResult.error);
        ctx.onUploadError?.(uploadResult.error);
        markPromptStatus(prompt.id, 'done');
        throw new Error(String(uploadResult.error));
      }
      updatePromptItem(prompt.id, (latestPrompt) => {
        if (!latestPrompt.prompt) return latestPrompt;
        const latestAudioPrompt = latestPrompt.prompt as SourceAudio;
        const { _tempFile, ...promptWithoutTemp } = latestAudioPrompt;
        return {
          ...latestPrompt,
          status: 'done',
          prompt: {
            ...promptWithoutTemp,
            id: uploadResult.id ?? promptWithoutTemp.id,
            src: uploadResult.uri,
            preview: promptWithoutTemp.preview || uploadResult.uri,
          },
        };
      });
    },
  }));
}

export function getToolTempSubtitlePendingItems(
  refs: TempUploadRefs,
  helpers: FormPromptListHelpers<FormSubtitlePrompt>,
  ctx: { subtitlePrompts: FormSubtitlePrompt[] },
): TempUploadItem[] {
  const { updatePromptItem, markPromptStatus } = helpers;
  const pendingUploads = ctx.subtitlePrompts
    .map((prompt) => {
      if (!prompt || !prompt.prompt?._tempFile) return null;
      const uploadKey = toolTempUploadKey('subtitle', prompt.id);
      if (isKeyScheduled(refs, uploadKey)) return null;
      return { prompt, uploadKey };
    })
    .filter((item): item is { prompt: FormSubtitlePrompt; uploadKey: string } => item !== null);

  return pendingUploads.map(({ prompt, uploadKey }) => ({
    uploadKey,
    run: async (): Promise<void> => {
      markPromptStatus(prompt.id, 'uploading');
      const currentPrompt = prompt.prompt;
      if (!currentPrompt?._tempFile) {
        markPromptStatus(prompt.id, 'done');
        return;
      }
      const uploadResult = await uploadFile({
        file: currentPrompt._tempFile,
        width: 0,
        height: 0,
        scene: UPLOAD_SCENE.VIDU,
      });
      if (uploadResult.error || !uploadResult.uri) {
        console.error('Failed to upload temp subtitle:', prompt.id, uploadResult.error);
        markPromptStatus(prompt.id, 'done');
        throw new Error(String(uploadResult.error));
      }
      updatePromptItem(prompt.id, (latestPrompt) => {
        if (!latestPrompt.prompt) return latestPrompt;
        const latestSubtitlePrompt = latestPrompt.prompt as SourceSubtitle;
        const { _tempFile, ...promptWithoutTemp } = latestSubtitlePrompt;
        return {
          ...latestPrompt,
          status: 'done',
          prompt: { ...promptWithoutTemp, src: uploadResult.uri },
        };
      });
    },
  }));
}

export function getToolTempVideosPendingItems(
  refs: TempUploadRefs,
  helpers: FormPromptListHelpers<FormVideoPrompt>,
  ctx: { videoPrompts: Array<FormVideoPrompt | null> },
  scene: UploadScene = UPLOAD_SCENE.VIDU,
): TempUploadItem[] {
  const { updatePromptItem, markPromptStatus } = helpers;
  const pendingUploads = ctx.videoPrompts
    .map((prompt) => {
      const currentPrompt = prompt?.prompt as TempVideoPrompt | null;
      if (!prompt || !currentPrompt?._tempFile) return null;
      const uploadKey = toolTempUploadKey('video', prompt.id);
      if (isKeyScheduled(refs, uploadKey)) return null;
      return { prompt, uploadKey };
    })
    .filter((item): item is { prompt: FormVideoPrompt; uploadKey: string } => item !== null);

  return pendingUploads.map(({ prompt, uploadKey }) => ({
    uploadKey,
    run: async (): Promise<void> => {
      markPromptStatus(prompt.id, 'uploading');
      const currentPrompt = prompt.prompt as TempVideoPrompt | null;
      if (!currentPrompt?._tempFile) {
        markPromptStatus(prompt.id, 'done');
        return;
      }
      const [videoUploadResult, coverUploadResult] = await uploadVideoFileWithCover(
        currentPrompt._tempFile,
        { width: currentPrompt.width ?? 0, height: currentPrompt.height ?? 0, duration: currentPrompt.duration },
        currentPrompt._tempCoverImage,
        scene,
      );
      if (videoUploadResult.error || !videoUploadResult.uri) {
        console.error('Failed to upload temp video:', prompt.id, videoUploadResult.error);
        markPromptStatus(prompt.id, 'done');
        throw new Error(String(videoUploadResult.error));
      }
      updatePromptItem(prompt.id, (latestPrompt) => {
        if (!latestPrompt.prompt) return latestPrompt;
        const latestVideoPrompt = latestPrompt.prompt as TempVideoPrompt;
        const { _tempFile, _tempCoverImage, ...promptWithoutTemp } = latestVideoPrompt;
        return {
          ...latestPrompt,
          status: 'done',
          prompt: {
            ...promptWithoutTemp,
            videoId: videoUploadResult.id ?? promptWithoutTemp.videoId,
            video: videoUploadResult.uri,
            videoPreview: promptWithoutTemp.videoPreview || videoUploadResult.uri,
            coverImage:
              !coverUploadResult || coverUploadResult.error || !coverUploadResult.uri
                ? promptWithoutTemp.coverImage
                : coverUploadResult.uri,
            coverImagePreview:
              promptWithoutTemp.coverImagePreview || promptWithoutTemp.coverImage || promptWithoutTemp.videoPreview,
          },
        };
      });
    },
  }));
}

export function getToolTempImagesPendingItems(
  refs: TempUploadRefs,
  helpers: FormPromptListHelpers<FormImagePrompt>,
  ctx: { imagePrompts: Array<FormImagePrompt | null> },
): TempUploadItem[] {
  const { updatePromptItem, markPromptStatus } = helpers;
  const pendingUploads = ctx.imagePrompts
    .map((prompt) => {
      if (!prompt?.prompt) return null;
      const tempData = extractTempImageData(prompt.prompt);
      const hasTempSelectedImage = !!prompt.prompt._tempSelectedImage;
      if (!tempData && !hasTempSelectedImage) return null;
      const uploadKey = tempData
        ? toolTempUploadKey('image', prompt.id, tempData.id)
        : toolTempUploadKey('image-selected', prompt.id);
      if (isKeyScheduled(refs, uploadKey)) return null;
      return { prompt, tempData, uploadKey };
    })
    .filter(
      (
        item,
      ): item is {
        prompt: FormImagePrompt;
        tempData: NonNullable<ReturnType<typeof extractTempImageData>> | null;
        uploadKey: string;
      } => item !== null,
    );

  return pendingUploads.map(({ prompt, tempData, uploadKey }) => ({
    uploadKey,
    run: async (): Promise<void> => {
      markPromptStatus(prompt.id, 'uploading');
      if (tempData) {
        const results = await uploadTempImages([tempData]);
        const result = results.get(tempData.id);
        const promptData = prompt.prompt;
        if (!result || !promptData) {
          console.error('Failed to upload temp image:', tempData.id);
          markPromptStatus(prompt.id, 'done');
          throw new Error('Upload temp image failed');
        }
        updatePromptItem(prompt.id, (latestPrompt) => ({
          ...latestPrompt,
          status: 'done',
          prompt: buildUploadedImageFields(promptData, result),
        }));
        return;
      }
      const currentPrompt = prompt.prompt;
      if (!currentPrompt?._tempSelectedImage) {
        markPromptStatus(prompt.id, 'done');
        return;
      }
      const selectedUploadResult = await uploadFile({
        file: currentPrompt._tempSelectedImage,
        width: currentPrompt.selectedImageWidth ?? currentPrompt.sourceImageWidth ?? 0,
        height: currentPrompt.selectedImageHeight ?? currentPrompt.sourceImageHeight ?? 0,
        scene: UPLOAD_SCENE.VIDU,
      });
      if (selectedUploadResult.error || !selectedUploadResult.uri) {
        console.error('Failed to upload temp selected image:', prompt.id, selectedUploadResult.error);
        markPromptStatus(prompt.id, 'done');
        throw new Error(String(selectedUploadResult.error));
      }
      updatePromptItem(prompt.id, (latestPrompt) => {
        if (!latestPrompt.prompt) return latestPrompt;
        const { _tempSelectedImage, ...rest } = latestPrompt.prompt;
        return {
          ...latestPrompt,
          status: 'done',
          prompt: {
            ...rest,
            selectedId: selectedUploadResult.id ?? rest.selectedId,
            selectedImage: selectedUploadResult.uri,
            selectedImagePreview: rest.selectedImagePreview || rest.sourceImagePreview,
          } as ImagePrompt,
        };
      });
    },
  }));
}

export function getToolTempSourceImageItems(
  refs: TempUploadRefs,
  ctx: {
    imagePrompt: ImagePrompt | null;
    getImagePrompt: () => ImagePrompt | null;
    setImagePrompt: (imagePrompt: ImagePrompt | null) => void;
    onStatusChange?: (status: 'uploading' | 'done') => void;
  },
): TempUploadItem[] {
  const { imagePrompt, getImagePrompt, setImagePrompt, onStatusChange } = ctx;
  if (!imagePrompt) return [];
  const tempData = extractTempImageData(imagePrompt);
  const hasTempSelectedImage = !!imagePrompt._tempSelectedImage;
  if (!tempData && !hasTempSelectedImage) return [];
  const uploadKey = tempData
    ? toolTempUploadKey('source-image', tempData.id)
    : toolTempUploadKey('source-image-selected', String(imagePrompt.selectedId || imagePrompt.sourceImage));
  if (isKeyScheduled(refs, uploadKey)) return [];

  return [
    {
      uploadKey,
      run: async (): Promise<void> => {
        onStatusChange?.('uploading');
        try {
          const currentImagePrompt = getImagePrompt();
          if (!currentImagePrompt) {
            return;
          }
          if (tempData) {
            const results = await uploadTempImages([tempData]);
            const result = results.get(tempData.id);
            if (!result) {
              console.error('Failed to upload temp source image:', tempData.id);
              throw new Error('Upload temp source image failed');
            }
            setImagePrompt(buildUploadedImageFields(currentImagePrompt, result));
            return;
          }
          if (!currentImagePrompt._tempSelectedImage) {
            return;
          }
          const selectedUploadResult = await uploadFile({
            file: currentImagePrompt._tempSelectedImage,
            width: currentImagePrompt.selectedImageWidth ?? currentImagePrompt.sourceImageWidth ?? 0,
            height: currentImagePrompt.selectedImageHeight ?? currentImagePrompt.sourceImageHeight ?? 0,
            scene: UPLOAD_SCENE.VIDU,
          });
          if (selectedUploadResult.error || !selectedUploadResult.uri) {
            console.error('Failed to upload temp selected source image:', selectedUploadResult.error);
            throw new Error(String(selectedUploadResult.error));
          }
          const latestImagePrompt = getImagePrompt();
          if (!latestImagePrompt) {
            return;
          }
          const { _tempSelectedImage, ...rest } = latestImagePrompt;
          setImagePrompt({
            ...rest,
            selectedId: selectedUploadResult.id ?? rest.selectedId,
            selectedImage: selectedUploadResult.uri,
            selectedImagePreview: rest.selectedImagePreview || rest.sourceImagePreview,
          });
        } finally {
          onStatusChange?.('done');
        }
      },
    },
  ];
}

export function getToolTempSourceVideoItems(
  refs: TempUploadRefs,
  ctx: {
    sourceVideo: SourceVideo | null;
    getSourceVideo: () => SourceVideo | null;
    setSourceVideo: (video: SourceVideo | null) => void;
    onStatusChange?: (status: 'uploading' | 'done') => void;
  },
): TempUploadItem[] {
  const { sourceVideo, getSourceVideo, setSourceVideo, onStatusChange } = ctx;
  if (!sourceVideo?._tempFile) return [];
  const uploadKey = toolTempUploadKey(
    'source-video',
    String(sourceVideo.tempId || sourceVideo.id || sourceVideo.preview),
  );
  if (isKeyScheduled(refs, uploadKey)) return [];

  return [
    {
      uploadKey,
      run: async (): Promise<void> => {
        onStatusChange?.('uploading');
        try {
          const currentSourceVideo = getSourceVideo();
          if (!currentSourceVideo?._tempFile) {
            return;
          }
          const [videoUploadResult, coverUploadResult] = await uploadVideoFileWithCover(
            currentSourceVideo._tempFile,
            {
              width: currentSourceVideo.width ?? 0,
              height: currentSourceVideo.height ?? 0,
              duration: currentSourceVideo.duration,
            },
            currentSourceVideo._tempCoverImage,
          );
          if (videoUploadResult.error || !videoUploadResult.uri) {
            console.error('Failed to upload temp source video:', videoUploadResult.error);
            throw new Error(String(videoUploadResult.error));
          }
          const latestSourceVideo = getSourceVideo();
          if (!latestSourceVideo) {
            return;
          }
          const { _tempFile, _tempCoverImage, ...videoWithoutTemp } = latestSourceVideo;
          setSourceVideo({
            ...videoWithoutTemp,
            id: videoUploadResult.id ?? videoWithoutTemp.id,
            src: videoUploadResult.uri,
            preview: videoWithoutTemp.preview || videoUploadResult.uri,
            cover_url:
              !coverUploadResult || coverUploadResult.error || !coverUploadResult.uri
                ? videoWithoutTemp.cover_url
                : coverUploadResult.uri,
            cover_preview_url:
              videoWithoutTemp.cover_preview_url || videoWithoutTemp.cover_url || videoWithoutTemp.preview,
          });
        } finally {
          onStatusChange?.('done');
        }
      },
    },
  ];
}
